import type { Prov, Provfraga, Svarsutrymme } from '../types'
import UtskriftPortal, { useUtskrift } from './UtskriftPortal'
import SvarsutrymmeVy from './SvarsutrymmeVy'

const TYP_ETIKETT: Record<string, string> = {
  flerval: 'Flerval',
  kortsvar: 'Kortsvar',
  resonemang: 'Resonemang',
}

const BOKSTAVER = ['a', 'b', 'c', 'd']

/** Kort klartextbeskrivning av frågans svarsutrymme, visad i lärarens läsvy
 *  så att det syns hur eleven svarar i utskriften. */
function svarsutrymmeEtikett(svarsutrymme: Svarsutrymme): string {
  switch (svarsutrymme.typ) {
    case 'kryssrutor':
      return 'Kryssrutor'
    case 'rader':
      return `${svarsutrymme.antal ?? 3} svarsrader`
    case 'numrerad_lista':
      return `${svarsutrymme.antal ?? 3} numrerade svar`
    case 'ruta':
      return 'Svarsruta'
    default:
      return 'Inget svarsutrymme'
  }
}

/** Exportformaten är datadrivna så att listan kan växa (Kahoot, Google Forms,
 *  exam.net m.fl.) utan att komponenten byggs om — varje format är en funktion
 *  från provdata till fil. */
const EXPORTFORMAT: { id: string; etikett: string; filnamn: (p: Prov) => string; mime: string; generera: (p: Prov) => string }[] = [
  {
    id: 'csv',
    etikett: 'CSV',
    filnamn: (p) => `${p.prov_id}.csv`,
    mime: 'text/csv;charset=utf-8',
    generera: (p) => {
      const falt = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`
      const rader = [
        ['fraga_id', 'typ', 'niva', 'poang', 'fraga', 'alternativ', 'ratt_svar_index', 'facit', 'kalla_referens'].join(';'),
        ...p.fragor.map((f) =>
          [
            falt(f.fraga_id),
            falt(f.typ),
            falt(f.niva),
            falt(f.poang),
            falt(f.fraga),
            falt(f.alternativ?.join(' | ')),
            falt(f.ratt_svar_index),
            falt(f.facit),
            falt(f.kalla_referens),
          ].join(';'),
        ),
      ]
      // BOM så att Excel läser åäö rätt.
      return '﻿' + rader.join('\n')
    },
  },
]

function laddaNer(innehall: string, filnamn: string, mime: string) {
  const blob = new Blob([innehall], { type: mime })
  const url = URL.createObjectURL(blob)
  const lank = document.createElement('a')
  lank.href = url
  lank.download = filnamn
  lank.click()
  URL.revokeObjectURL(url)
}

function FragaKort({ fraga, nummer }: { fraga: Provfraga; nummer: number }) {
  return (
    <div className="provfraga">
      <div className="provfraga-meta">
        <span className="provfraga-nr">{nummer}.</span>
        <span className={`niva-badge niva-${fraga.niva}`} title={`Nivå ${fraga.niva}`}>
          {fraga.niva}
        </span>
        <span className="formaga-chip">{TYP_ETIKETT[fraga.typ] ?? fraga.typ}</span>
        <span className="formaga-chip">{svarsutrymmeEtikett(fraga.svarsutrymme)}</span>
        <span className="provfraga-poang">
          {fraga.poang} {fraga.poang === 1 ? 'poäng' : 'poäng'}
        </span>
      </div>
      <p className="provfraga-text">{fraga.fraga}</p>
      {fraga.alternativ && (
        <ol className="provfraga-alternativ">
          {fraga.alternativ.map((alt, i) => (
            <li key={i}>
              <span className="alternativ-bokstav">{BOKSTAVER[i]})</span> {alt}
            </li>
          ))}
        </ol>
      )}
      <details className="facit">
        <summary>Visa facit</summary>
        <p>
          {fraga.typ === 'flerval' && fraga.ratt_svar_index !== undefined && (
            <strong>Rätt svar: {BOKSTAVER[fraga.ratt_svar_index]}) — </strong>
          )}
          {fraga.facit}
          {fraga.kalla_referens && <em className="provfraga-kalla"> ({fraga.kalla_referens})</em>}
        </p>
      </details>
    </div>
  )
}

export default function ProvSektion({ prov }: { prov: Prov }) {
  const utskrift = useUtskrift()
  const totalPoang = prov.fragor.reduce((s, f) => s + f.poang, 0)

  return (
    <section aria-labelledby="prov-rubrik">
      <div className="sektionshuvud-med-status">
        <h2 className="sektionsrubrik" id="prov-rubrik">Prov</h2>
      </div>
      <p className="sektionsingress">
        {prov.titel} — {prov.fragor.length} frågor, {totalPoang} poäng
        {prov.rekommenderad_tid_min && `, cirka ${prov.rekommenderad_tid_min} minuter`}. Facit och
        källhänvisningar visas bakom utfällning per fråga och följer inte med i elevutskriften.
      </p>

      <div className="knapprad prov-knappar">
        <button type="button" className="knapp knapp-primar knapp-liten" onClick={utskrift.skrivUt}>
          🖨 Skriv ut provet
        </button>
        {EXPORTFORMAT.map((format) => (
          <button
            key={format.id}
            type="button"
            className="knapp knapp-sekundar knapp-liten"
            onClick={() => laddaNer(format.generera(prov), format.filnamn(prov), format.mime)}
          >
            ⬇ Exportera {format.etikett}
          </button>
        ))}
      </div>

      <div className="provlista">
        {prov.fragor.map((fraga, i) => (
          <FragaKort key={fraga.fraga_id} fraga={fraga} nummer={i + 1} />
        ))}
      </div>

      {utskrift.aktiv && (
        <UtskriftPortal onKlar={utskrift.avsluta}>
          <h1>{prov.titel}</h1>
          <p>
            <strong>Totalt: {totalPoang} poäng.</strong>
            {prov.rekommenderad_tid_min && ` Tid: cirka ${prov.rekommenderad_tid_min} minuter.`} Namn:
            _______________________ Klass: _________
          </p>
          {prov.fragor.map((fraga, i) => (
            <div key={fraga.fraga_id} className="print-provfraga">
              <p>
                <strong>
                  {i + 1}. ({fraga.poang} p)
                </strong>{' '}
                {fraga.fraga}
              </p>
              {fraga.alternativ && (
                <ul className="print-alternativ">
                  {fraga.alternativ.map((alt, j) => (
                    <li key={j}>
                      {fraga.svarsutrymme.typ === 'kryssrutor' && <span className="kryssruta" aria-hidden="true" />}
                      {BOKSTAVER[j]}) {alt}
                    </li>
                  ))}
                </ul>
              )}
              <SvarsutrymmeVy svarsutrymme={fraga.svarsutrymme} />
            </div>
          ))}
        </UtskriftPortal>
      )}
    </section>
  )
}
